/**
 * Admin API Client: page-level calls into admin-service. Every function takes
 * the caller's ID token and goes through the shared request core
 * (`utils/request.ts`), which owns timeouts, the 401 retry and the error shape.
 */
import { adminRequest, adminRequestRaw, AdminApiError } from './request';
import type { AuditLogsResponse } from '@/types';

export { AdminApiError };

/**
 * Authenticated JSON request with an explicit token.
 * @param endpoint - path under the admin API base
 * @param idToken - the signed-in admin's Firebase ID token
 */
function adminFetch<T = any>(
  endpoint: string,
  idToken: string,
  options: RequestInit = {}
): Promise<T> {
  return adminRequest<T>(endpoint, { ...options, token: idToken });
}

// User Management
export const getUsers = (
  idToken: string,
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    plan?: string;
    status?: string;
  }
) => {
  const queryParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, String(value));
    });
  }
  const query = queryParams.toString() ? `?${queryParams}` : '';
  return adminFetch(`/users${query}`, idToken);
};

export const getUser = (idToken: string, userId: string) =>
  adminFetch(`/users/${userId}`, idToken);

export const updateUser = (
  idToken: string,
  userId: string,
  updates: { displayName?: string; status?: 'active' | 'suspended' | 'banned' }
) =>
  adminFetch(`/users/${userId}`, idToken, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });

/**
 * Grant or remove the platform `admin` claim (console access) on a user.
 * The backend refuses removing your own access (400).
 */
export const setPlatformAdmin = (
  idToken: string,
  userId: string,
  admin: boolean
): Promise<{ success: boolean; userId: string; isPlatformAdmin: boolean }> =>
  adminFetch(`/users/${userId}/platform-admin`, idToken, {
    method: 'PUT',
    body: JSON.stringify({ admin }),
  });

// Credit Management
export const adjustCredits = (
  idToken: string,
  userId: string,
  data: {
    amount: number;
    type: 'grant' | 'deduct' | 'set';
    reason: string;
  }
) =>
  adminFetch(`/users/${userId}/adjust-credits`, idToken, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const getCreditHistory = (idToken: string, userId: string, limit = 50) =>
  adminFetch(`/users/${userId}/credit-history?limit=${limit}`, idToken);

// Job Management
export const getJobs = (
  idToken: string,
  params?: {
    status?: string;
    userId?: string;
    organizationId?: string;
    minAgeMins?: number;
    page?: number;
    limit?: number;
  }
) => {
  const queryParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '')
        queryParams.append(key, String(value));
    });
  }
  const query = queryParams.toString() ? `?${queryParams}` : '';
  return adminFetch(`/jobs${query}`, idToken);
};

export const cancelJob = (idToken: string, jobId: string, reason?: string) =>
  adminFetch(`/jobs/${jobId}/cancel`, idToken, {
    method: 'POST',
    body: JSON.stringify({ reason: reason || 'Cancelled by admin' }),
  });

export const bulkCancelJobs = (
  idToken: string,
  jobIds: string[],
  reason?: string
) =>
  adminFetch('/jobs/bulk-cancel', idToken, {
    method: 'POST',
    body: JSON.stringify({
      jobIds,
      reason: reason || 'Bulk cancelled by admin',
    }),
  });

// Analytics
export const getAnalyticsOverview = (idToken: string) =>
  adminFetch('/analytics/overview', idToken);

export const getUsageAnalytics = (
  idToken: string,
  params?: {
    startDate?: string;
    endDate?: string;
    groupBy?: string;
  }
) => {
  const queryParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, value);
    });
  }
  const query = queryParams.toString() ? `?${queryParams}` : '';
  return adminFetch(`/analytics/usage${query}`, idToken);
};

// Cost Analytics (admin-only) — provider cost vs. credits charged
export interface CostAnalyticsRow {
  model?: string;
  provider?: string;
  organizationId?: string;
  organizationName?: string;
  day?: string;
  calls: number;
  creditsCharged: number;
  providerCostMicros: number;
  revenueMicros: number;
  marginMicros: number;
  marginRatio: number | null;
  providerCostUsd: string;
  revenueUsd: string;
  marginUsd: string;
  callsWithoutCost?: number;
  /** SPRITE-1307: runs that failed model-side (0 credits, real provider cost). */
  failedCalls?: number;
  failedProviderCostMicros?: number;
  failedProviderCostUsd?: string;
  /** Margin on billed runs only (excludes failed-run spend). */
  pricingMarginMicros?: number;
  negativeMargin?: boolean;
}

export interface CostAnalyticsResponse {
  window: { startDate: string; endDate: string };
  usdPerCreditMicros: number;
  rows: CostAnalyticsRow[];
  totals?: CostAnalyticsRow & { callsWithoutCost: number };
}

export interface PricingRegistryRow {
  modelId: string;
  provider: string;
  pricingModel: string;
  perCallCostMicros: number | null;
  perCallCostUsd: string | null;
  perSecondCostMicros: number | null;
  perThousandCharsCostMicros: number | null;
  source: string;
  lastVerified: string;
  notes: string | null;
}

function buildCostQuery(params?: {
  startDate?: string;
  endDate?: string;
  model?: string;
  provider?: string;
  limit?: number;
}) {
  if (!params) return '';
  const qp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qp.append(k, String(v));
  });
  const qs = qp.toString();
  return qs ? `?${qs}` : '';
}

export const getCostAnalyticsByModel = (
  idToken: string,
  params?: { startDate?: string; endDate?: string }
): Promise<CostAnalyticsResponse> =>
  adminFetch(`/analytics/costs${buildCostQuery(params)}`, idToken);

export const getCostAnalyticsTimeseries = (
  idToken: string,
  params?: {
    startDate?: string;
    endDate?: string;
    model?: string;
    provider?: string;
  }
): Promise<CostAnalyticsResponse> =>
  adminFetch(`/analytics/costs/timeseries${buildCostQuery(params)}`, idToken);

export const getCostAnalyticsByOrganization = (
  idToken: string,
  params?: { startDate?: string; endDate?: string; limit?: number }
): Promise<CostAnalyticsResponse> =>
  adminFetch(
    `/analytics/costs/by-organization${buildCostQuery(params)}`,
    idToken
  );

/** SPRITE-1412: who started the job — the web app or a connected AI agent. */
export type CostSource = 'web' | 'agent';

export interface CostBySourceRow extends CostAnalyticsRow {
  source: CostSource;
}

/** Spend through one AI-agent connection (OAuth grant). */
export interface AgentConnectionCostRow extends CostAnalyticsRow {
  grantId: string;
  /** Null once the grant row is gone (its member was deleted). */
  clientName: string | null;
  revokedAt: string | null;
}

export interface CostBySourceResponse {
  window: { startDate: string; endDate: string };
  usdPerCreditMicros: number;
  /** Always one row per source; rows from before SPRITE-1399 count as web. */
  bySource: CostBySourceRow[];
  topConnections: AgentConnectionCostRow[];
}

export const getCostAnalyticsBySource = (
  idToken: string,
  params?: { startDate?: string; endDate?: string; limit?: number }
): Promise<CostBySourceResponse> =>
  adminFetch(`/analytics/costs/by-source${buildCostQuery(params)}`, idToken);

export const getPricingRegistry = (
  idToken: string
): Promise<{ rows: PricingRegistryRow[] }> =>
  adminFetch('/analytics/costs/pricing', idToken);

/** SPRITE-1468: where the credits in a window came from. */
export type CreditGrantBucket = 'purchase' | 'signup' | 'admin_grant';

export interface CreditsGrantedRow {
  bucket: CreditGrantBucket;
  /** Number of top-up events. */
  grants: number;
  credits: number;
  /** Credits valued at the list price (USD micros). */
  valueMicros: number;
  valueUsd: string;
}

export interface CreditsGrantedResponse {
  window: { startDate: string; endDate: string };
  usdPerCreditMicros: number;
  /** Always purchase, signup, admin_grant in that order (zeros when empty). */
  rows: CreditsGrantedRow[];
  totals: Omit<CreditsGrantedRow, 'bucket'>;
  /** Credits deducted in the same window, for context. */
  creditsConsumed: number;
}

export const getCreditsGranted = (
  idToken: string,
  params?: { startDate?: string; endDate?: string }
): Promise<CreditsGrantedResponse> =>
  adminFetch(
    `/analytics/costs/credits-granted${buildCostQuery(params)}`,
    idToken
  );

// Model kill-switch (SPRITE-997) and catalogue

/** A model-registry entry, as stored in the D1 catalogue. */
export type ModelConfig = Record<string, unknown> & {
  id: string;
  name: string;
  category: string;
  pickerOrder?: number;
};

/** A provider-cost record (services/shared/model-pricing.js shape). */
export type ModelPricing = Record<string, unknown> & {
  modelId: string;
  provider: string;
  pricingModel: string;
};

/** A licence verdict (CLAUDE-DOC:MODEL_LICENSING in model-audit.js). */
export type ModelLicensing = Record<string, unknown> & {
  status: 'verified' | 'unverified' | 'prohibited';
};

/** A strong doc caveat (CLAUDE-DOC:MODEL_CAVEATS in model-audit.js). */
export type ModelCaveat = Record<string, unknown> & { guidance: string };

export interface ModelRow {
  id: string;
  name: string;
  category: string;
  provider: string;
  disabled: boolean;
  disabledBy: string | null;
  disabledReason: string | null;
  updatedAt: string | null;
  // Disabled but never toggled here: a code-level `disabled: true` in model-registry.js, not a kill switch.
  isStaticDisable: boolean;
  /** Stored in the D1 catalogue (editable here) rather than in code. */
  isCatalogue: boolean;
  retiredAt: string | null;
  /** First enabled; until then the public catalogue leaves the model out. */
  releasedAt: string | null;
  pickerOrder: number | null;
  /** The stored row's version; an edit sends it back (409 if it moved on). */
  catalogVersion: number | null;
  /** Why this release can't apply the stored row, if it can't. */
  catalogueProblem: string | null;
  catalogUpdatedBy: string | null;
  catalogUpdatedAt: string | null;
  registryGroup: string | null;
  config: ModelConfig;
  pricing: ModelPricing | null;
  audit: { licensing: ModelLicensing | null; caveats: ModelCaveat[] };
}

/** What the deployed workers can run; the editor offers only these. */
export interface ModelCatalogueSchema {
  groups: Array<{ group: string; categories: string[] }>;
  pickers: Array<{
    picker: string;
    category: string;
    feature?: string;
    modelIds: string[];
  }>;
  inputProfiles: Record<string, string[]>;
  optionGroups: string[];
  cutoutPreparations: string[];
  etaTiers: string[];
  regionRestrictions: string[];
  pricingModels: Record<string, string[]>;
}

/** A catalogue write: a new model, or the parts of one being replaced. */
export interface ModelCatalogueWrite {
  registryGroup?: string;
  config?: ModelConfig;
  pricing?: ModelPricing;
  licensing?: ModelLicensing;
  caveats?: ModelCaveat[];
}

export const getModels = (idToken: string): Promise<{ models: ModelRow[] }> =>
  adminFetch('/models', idToken);

export const disableModel = (
  idToken: string,
  modelId: string,
  reason?: string
) =>
  adminFetch(`/models/${modelId}/disable`, idToken, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });

export const enableModel = (idToken: string, modelId: string) =>
  adminFetch(`/models/${modelId}/enable`, idToken, {
    method: 'POST',
  });

export const getModelCatalogueSchema = (
  idToken: string
): Promise<ModelCatalogueSchema> =>
  adminFetch('/models/catalogue-schema', idToken);

export const createCatalogueModel = (
  idToken: string,
  write: Required<Omit<ModelCatalogueWrite, 'caveats'>> &
    Pick<ModelCatalogueWrite, 'caveats'>
) =>
  adminFetch('/models', idToken, {
    method: 'POST',
    body: JSON.stringify(write),
  });

export const updateCatalogueModel = (
  idToken: string,
  modelId: string,
  write: Omit<ModelCatalogueWrite, 'registryGroup'> & {
    expectedCatalogVersion: number;
  }
) =>
  adminFetch(`/models/${encodeURIComponent(modelId)}`, idToken, {
    method: 'PUT',
    body: JSON.stringify(write),
  });

export const reorderModelPicker = (
  idToken: string,
  picker: string,
  modelIds: string[]
) =>
  adminFetch('/models/picker-order', idToken, {
    method: 'POST',
    body: JSON.stringify({ picker, modelIds }),
  });

export const setModelRetired = (
  idToken: string,
  modelId: string,
  retired: boolean
) =>
  adminFetch(
    `/models/${encodeURIComponent(modelId)}/${retired ? 'retire' : 'unretire'}`,
    idToken,
    { method: 'POST' }
  );

// Refund/dispute credit clawbacks (SPRITE-613)
export interface CreditClawbackRow {
  id: string;
  organizationId: string;
  organizationName: string;
  userId: string;
  paymentIntentId: string;
  stripeEventId: string;
  triggerType: 'refund' | 'dispute';
  creditsClawedBack: number;
  // Credits already spent when the money was refunded: unrecoverable, flagged for finance.
  shortfallCredits: number;
  amountRefundedCents: number | null;
  // Lower-case ISO 4217 code of amountRefundedCents (customer currency under Adaptive
  // Pricing, SPRITE-1239); null on rows predating the column.
  currency: string | null;
  creditsBefore: number | null;
  creditsAfter: number | null;
  reason: string | null;
  createdAt: string;
  // SPRITE-1154 repeat-offender context.
  stripeCustomerId: string | null;
  // ALL-TIME counts of distinct clawed-back payment intents (partial refunds of one
  // purchase count once): for this org, then across the Stripe customer's orgs.
  organizationClawbackCount: number | null;
  customerClawbackCount: number | null;
  // Distinct orgs with clawbacks for this customer; ≥ 2 means a multi-org chargeback.
  customerOrganizationCount: number | null;
  // SPRITE-1155: org suspension timestamp, null while the org is active.
  organizationSuspendedAt: string | null;
}

export interface CreditClawbacksResponse {
  window: { startDate: string; endDate: string };
  rows: CreditClawbackRow[];
  totals: { creditsClawedBack: number; shortfallCredits: number };
}

export const getCreditClawbacks = (
  idToken: string,
  params?: { startDate?: string; endDate?: string; limit?: number }
): Promise<CreditClawbacksResponse> =>
  adminFetch(`/analytics/costs/clawbacks${buildCostQuery(params)}`, idToken);

// Audit Logs
export const getAuditLogs = (
  idToken: string,
  params?: {
    page?: number;
    limit?: number;
    action?: string;
    severity?: string;
    /** SPRITE-1468: only rows whose target is this user. */
    targetUserId?: string;
    startDate?: string;
    endDate?: string;
  }
): Promise<AuditLogsResponse> => {
  const queryParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '')
        queryParams.append(key, String(value));
    });
  }
  const query = queryParams.toString() ? `?${queryParams}` : '';
  return adminFetch(`/audit-logs${query}`, idToken);
};

// Bulk Actions
export const bulkUpdateUsers = (
  idToken: string,
  userIds: string[],
  updates: any
) =>
  adminFetch('/users/bulk-update', idToken, {
    method: 'POST',
    body: JSON.stringify({ userIds, updates }),
  });

// Data Export

/**
 * Download every user as a file (the backend builds it: uid, email, display
 * name, organisation, credits, created, updated). Returns the file body.
 */
export const getUsersExport = async (
  idToken: string,
  format: 'csv' | 'json' = 'csv'
): Promise<Blob> => {
  const response = await adminRequestRaw(`/export/users?format=${format}`, {
    token: idToken,
  });
  return response.blob();
};

export const exportAuditLogs = async (
  idToken: string,
  params?: {
    action?: string;
    severity?: string;
    startDate?: string;
    endDate?: string;
    format?: 'csv' | 'json';
  }
): Promise<Blob> => {
  const queryParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') queryParams.append(key, value);
    });
  }
  const query = queryParams.toString() ? `?${queryParams}` : '';
  const response = await adminRequestRaw(`/export/audit-logs${query}`, {
    token: idToken,
  });
  return response.blob();
};

// User Deletion
export const deleteUser = (idToken: string, userId: string) =>
  adminFetch(`/users/${userId}`, idToken, {
    method: 'DELETE',
  });

// Organization Management
export const getOrganizations = (
  idToken: string,
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    plan?: string;
    status?: string;
  }
) => {
  const queryParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, String(value));
    });
  }
  const query = queryParams.toString() ? `?${queryParams}` : '';
  return adminFetch(`/organizations${query}`, idToken);
};

export const getOrganization = (idToken: string, orgId: string) =>
  adminFetch(`/organizations/${orgId}`, idToken);

export const updateOrganization = (
  idToken: string,
  orgId: string,
  updates: any
) =>
  adminFetch(`/organizations/${orgId}`, idToken, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });

export const deleteOrganization = (idToken: string, orgId: string) =>
  adminFetch(`/organizations/${orgId}`, idToken, {
    method: 'DELETE',
  });

// Organization suspension (SPRITE-1155) — both endpoints are idempotent.
export const suspendOrganization = (
  idToken: string,
  orgId: string,
  reason?: string
) =>
  adminFetch(`/organizations/${orgId}/suspend`, idToken, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });

export const reinstateOrganization = (idToken: string, orgId: string) =>
  adminFetch(`/organizations/${orgId}/reinstate`, idToken, {
    method: 'POST',
  });

export const getOrganizationMembers = (idToken: string, orgId: string) =>
  adminFetch(`/organizations/${orgId}/members`, idToken);

export const addOrganizationMember = (
  idToken: string,
  orgId: string,
  data: {
    email: string;
    role: 'admin' | 'member';
  }
) =>
  adminFetch(`/organizations/${orgId}/members`, idToken, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const updateOrganizationMember = (
  idToken: string,
  orgId: string,
  memberId: string,
  updates: any
) =>
  adminFetch(`/organizations/${orgId}/members/${memberId}`, idToken, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });

export const removeOrganizationMember = (
  idToken: string,
  orgId: string,
  memberId: string
) =>
  adminFetch(`/organizations/${orgId}/members/${memberId}`, idToken, {
    method: 'DELETE',
  });

// Organization Credit Management
export const adjustOrganizationCredits = (
  idToken: string,
  orgId: string,
  data: {
    amount: number;
    type: 'grant' | 'deduct' | 'set';
    reason: string;
  }
) =>
  adminFetch(`/organizations/${orgId}/adjust-credits`, idToken, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const getOrganizationCreditHistory = (
  idToken: string,
  orgId: string,
  limit = 50
) =>
  adminFetch(`/organizations/${orgId}/credit-history?limit=${limit}`, idToken);

// Organization Project Management
export const getOrganizationProjects = (idToken: string, orgId: string) =>
  adminFetch(`/organizations/${orgId}/projects`, idToken);

export const adminTransferProject = (
  idToken: string,
  sourceOrgId: string,
  projectId: string,
  targetOrganizationId: string
) =>
  adminFetch(
    `/organizations/${sourceOrgId}/projects/${projectId}/transfer`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify({ targetOrganizationId }),
    }
  );

// Trash Management
export const getOrganizationTrash = (idToken: string, orgId: string) =>
  adminFetch(`/organizations/${orgId}/trash`, idToken);

export const restoreOrgProject = (
  idToken: string,
  orgId: string,
  projectId: string
) =>
  adminFetch(
    `/organizations/${orgId}/trash/projects/${projectId}/restore`,
    idToken,
    {
      method: 'POST',
    }
  );

export const restoreOrgAsset = (
  idToken: string,
  orgId: string,
  assetId: string
) =>
  adminFetch(
    `/organizations/${orgId}/trash/assets/${assetId}/restore`,
    idToken,
    {
      method: 'POST',
    }
  );

export const permanentlyDeleteOrgProject = (
  idToken: string,
  orgId: string,
  projectId: string
) =>
  adminFetch(`/organizations/${orgId}/trash/projects/${projectId}`, idToken, {
    method: 'DELETE',
  });

export const permanentlyDeleteOrgAsset = (
  idToken: string,
  orgId: string,
  assetId: string
) =>
  adminFetch(`/organizations/${orgId}/trash/assets/${assetId}`, idToken, {
    method: 'DELETE',
  });

// System Settings (SPRITE-1222)

export interface DiscordSignupWebhookSetting {
  webhookUrl: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export const getDiscordSignupWebhook = (
  idToken: string
): Promise<DiscordSignupWebhookSetting> =>
  adminFetch('/settings/discord-signup-webhook', idToken);

export const updateDiscordSignupWebhook = (
  idToken: string,
  webhookUrl: string
): Promise<{ success: boolean; webhookUrl: string }> =>
  adminFetch('/settings/discord-signup-webhook', idToken, {
    method: 'PUT',
    body: JSON.stringify({ webhookUrl }),
  });

// Failed-run spend alert (SPRITE-1307)

export interface FailedSpendAlertSetting {
  webhookUrl: string;
  thresholdUsd: number;
  windowHours: number;
  enabled: boolean;
  /** The stored value no longer validates; the alert is off until re-saved. */
  invalid: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

export const getFailedSpendAlertSetting = (
  idToken: string
): Promise<FailedSpendAlertSetting> =>
  adminFetch('/settings/failed-spend-alert', idToken);

export const updateFailedSpendAlertSetting = (
  idToken: string,
  config: { webhookUrl: string; thresholdUsd: number; windowHours: number }
): Promise<{
  success: boolean;
  webhookUrl: string;
  thresholdUsd: number;
  windowHours: number;
  enabled: boolean;
}> =>
  adminFetch('/settings/failed-spend-alert', idToken, {
    method: 'PUT',
    body: JSON.stringify(config),
  });
